// src/Services.js
import React from "react";
import { motion } from "framer-motion";

// Icons config
const tiles = [
  { title: "Design Consultancy for all kind of structures on turnkey basis", icon: ("./images/icons/i1.png") },
  { title: "Concept to GFC Management", icon: ("./images/icons/i2.png") },
  { title: "Design and site support for sales gallery, show apartments", icon: ("./images/icons/i3.png") },
  { title: "Value Engineering Support", icon: ("./images/icons/i4.png") },
  { title: "Site Design Support", icon: ("./images/icons/i5.png") },
  { title: "Design Audits & Reporting with site management", icon: ("./images/icons/i6.png") },
  { title: "Project feasibility Report", icon: ("./images/icons/i7.png") },
  { title: "Design, Authority & Stakeholder Communication", icon: ("./images/icons/i8.png") },
  { title: "Drawing, Documentation and timeline Control", icon: ("./images/icons/i9.png") },
];

// Easing and physics
const ease = [0.22, 1, 0.36, 1];

// Text orchestration
const textContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      delayChildren: 0.05,
      staggerChildren: 0.06,
      ease,
    },
  },
};

const textItem = {
  hidden: { opacity: 0, y: 12 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 160, damping: 24, mass: 0.8 },
  },
};

// Tiles orchestration
const gridContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      delayChildren: 0.2,
      staggerChildren: 0.08,
      ease,
    },
  },
};

// Optional wave staggering via custom (no child transition override)
const useWave = true;
const center = { cx: 1, cy: 1 }; // 3x3 center
const baseDelay = 0.20;

const tileItem = {
  hidden: { opacity: 0, y: 180, scale: 0.96 },
  show: (custom) => {
    const { i } = custom || { i: 0 };
    let extraDelay = 0;
    if (useWave) {
      const row = Math.floor(i / 3);
      const col = i % 18;
      const dx = col - center.cx;
      const dy = row - center.cy;
      const dist = Math.sqrt(dx * dx + dy * dy); // 0, 1, ~1.41, 2
      extraDelay = dist * baseDelay;
    }
    return {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 220,
        damping: 26,
        mass: 0.8,
        delay: extraDelay, // additive per-tile delay that preserves variant orchestration
      },
    };
  },
};

export default function Services() {
  return (
    <section
      style={{
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#030303ff",
        height: "520px",
      }}
      className="pt-[65px]"
    >
      {/* Background image */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${("./images/solutions2.jpg")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.80,
          alignItems: "stretch",
        }}
      />

      {/* Content layer */}
      <div
        style={{
          position: "relative",
          height: "100%",
          maxWidth: 1280,
          margin: "0 auto",
          padding: "32px 20px",
          display: "grid",
          gridTemplateColumns: "minmax(380px, 1.08fr) 1fr",
          gap: 24,
        }}
      >
        {/* Left text block */}
        <motion.div
          variants={textContainer}
          initial="hidden"
          animate="show"
          style={{ display: "grid", alignContent: "center" }}
        >
          <motion.h2
            variants={textItem}
            style={{
              color: "#ffffff",
              margin: "0 0 10px",
              fontSize: 56,
              lineHeight: 1,
              letterSpacing: 1,
              fontWeight: 800,
            }}
          >
            SOLUTIONS
          </motion.h2>

          <div style={{ maxWidth: 620 }}>
            <motion.h3
              variants={textItem}
              style={{ color: "#dfe3e6", margin: "0 0 8px", fontSize: 18, letterSpacing: 0.6 }}
            >
              DESIGN CONSULTANCY:
            </motion.h3>
            <motion.p
              variants={textItem}
              style={{ color: "#cbd1d5", margin: "0 0 16px", lineHeight: 1.7, fontSize: 16 }}
            >
              At MANAR, we bridge the gap between design vision and construction reality — transforming ideas into spaces that are buildable, market‑ready, and user‑focused.
            </motion.p>

            <motion.h3
              variants={textItem}
              style={{ color: "#dfe3e6", margin: "0 0 8px", fontSize: 18, letterSpacing: 0.6 }}
            >
              COORDINATION MANAGEMENT:
            </motion.h3>
            <motion.p
              variants={textItem}
              style={{ color: "#cbd1d5", margin: 0, lineHeight: 1.7, fontSize: 16 }}
            >
              We align all stakeholders, streamline communication, and drive efficiency across every phase of the project — ensuring clarity, speed, and quality from start to finish.
            </motion.p>
          </div>
        </motion.div>

        {/* Right 3×3 grid */}
        <motion.div
          variants={gridContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gridTemplateRows: "repeat(3, 1fr)",
            alignSelf: "center",
            height: "100%",
            width: "100%",
            align: "center",
            gap: 0,
          }}
        >
          {tiles.map((t, i) => {
            // Only the "gray" tiles become glass
            const dark = [0, 2, 4, 6, 8].includes(i);

            const tileStyle = dark
              ? {
                  position: "relative",
                  background: "rgba(58, 59, 59, 0.14)", // translucent gray pane
                  color: "#ffffff",
                  border: "1.5px solid rgba(255,255,255,0.2)", // subtle light edge
                  boxShadow: "0 8px 24px rgba(0,0,0,0.25)",   // soft depth
                  backdropFilter: "blur(12px) saturate(140%)", // frosted glass effect
                  WebkitBackdropFilter: "blur(12px) saturate(140%)", // Safari
                }
              : {
                  backgroundColor: "#F7F1E8",
                  color: "#33393d",
                  border: "1px solid rgba(0,0,0,0.06)",
                };

            return (
              <motion.div
                key={i}
                custom={{ i }}
                variants={tileItem}
                whileHover={{ scale: 1.02, y: -2, transition: { type: "spring", stiffness: 300, damping: 18 } }}
                style={{
                  display: "grid",
                  placeItems: "center",
                  padding: 12,
                  textAlign: "center",
                  // Optional radius to match glass style; tweak as desired:
                  borderRadius: 0,
                  ...tileStyle,
                }}
              >
                {/* Optional inner highlight overlay for nicer glass edge */}
                {dark && (
                  <div
                    aria-hidden
                    style={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: 0,
                      pointerEvents: "none",
                      background:
                        "linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.06) 60%, rgba(255,255,255,0) 100%)",
                    }}
                  />
                )}

                <div
                  style={{
                    display: "grid",
                    placeItems: "center",
                    gap: 10,
                    maxWidth: 220,
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <img
                    src={t.icon}
                    alt=""
                    style={{
                      width: 46,
                      height: 46,
                      objectFit: "contain",
                      // Keep icons legible on glass
                      filter: dark ? "invert(0.1)" : "none",
                    }}
                  />
                  <p style={{ margin: 0, fontSize: 12, lineHeight: 1.35, fontWeight: 600 }}>
                    {t.title}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
